import { memo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, ChevronDown, Wrench, CheckCircle2, FileText, RotateCcw } from 'lucide-react';
import type { Message } from '../../App';
import './MessageBubble.css';
import { AuthenticatedImage } from '../../components/AuthenticatedImage';
import AssistantMetaChip from './AssistantMetaChip';
import { useCurrentUser } from '../../core/auth/UserProvider';

type MessageBubbleProps = {
    message: Message;
    /** Only supplied for the last completed assistant bubble. When present,
     *  a Regenerate pill renders next to the Copy button. */
    onRegenerate?: (() => Promise<void>) | undefined;
};

function MessageBubbleImpl({ message, onRegenerate }: MessageBubbleProps) {
    const [toolExpanded, setToolExpanded] = useState(false);
    const [toolCallInputsExpanded, setToolCallInputsExpanded] = useState<Map<string, boolean>>(new Map());
    const currentUser = useCurrentUser();
    const isAdminViewer = currentUser?.access?.is_admin === true;
    const isTypingOnly =
        message.role === 'assistant' &&
        Boolean(message.streaming) &&
        (!message.content || message.content.trim().length === 0);

    const toggleToolCallInput = (toolCallId: string) => {
        setToolCallInputsExpanded((prev) => {
            const next = new Map(prev);
            next.set(toolCallId, !next.get(toolCallId));
            return next;
        });
    };

    // Tool Call Message (Assistant decides to use a tool)
    if (message.role === 'assistant' && message.tool_calls && message.tool_calls.length > 0) {
        return (
            <div className="message-bubble tool-call-bubble">
                {message.content && (
                    <div className="message-content assistant-text">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    </div>
                )}
                <div className="tool-call-container">
                    {message.tool_calls.filter((tc) => tc.name != null).map((tc) => {
                        const isInputExpanded = toolCallInputsExpanded.get(tc.id) ?? false;
                        const toolLabel = formatToolName(tc.name);
                        return (
                            <div key={tc.id} className="tool-call-card">
                                <button
                                    className="tool-call-header"
                                    onClick={() => toggleToolCallInput(tc.id)}
                                    type="button"
                                >
                                    <span className="tool-icon"><Wrench size={14} /></span>
                                    <span className="tool-name">{toolLabel}</span>
                                    <span className={`toggle-arrow ${isInputExpanded ? 'expanded' : ''}`}>
                                        <ChevronDown size={12} />
                                    </span>
                                </button>
                                {isInputExpanded && (
                                    <div className="tool-args">
                                        <pre>{formatToolResult(tc.arguments)}</pre>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Tool Result Message (Response from tool execution)
    if (message.role === 'tool') {
        const parsed = parseToolResult(message.content);
        return (
            <div className="message-bubble tool-result-bubble">
                <div className="tool-result-container">
                    <button
                        className="tool-result-toggle"
                        onClick={() => setToolExpanded(!toolExpanded)}
                        type="button"
                    >
                        <span className="tool-result-icon"><CheckCircle2 size={14} /></span>
                        {parsed.fileName ? (
                            <span className="tool-result-label">
                                <FileText size={14} />
                                {parsed.fileName}
                            </span>
                        ) : (
                            <span className="tool-result-label">Result</span>
                        )}
                        <span className={`toggle-arrow ${toolExpanded ? 'expanded' : ''}`}>
                            <ChevronDown size={12} />
                        </span>
                    </button>
                    {toolExpanded && (
                        <div className="tool-result-content">
                            {parsed.content ? (
                                <div className="tool-result-markdown">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{parsed.content}</ReactMarkdown>
                                </div>
                            ) : (
                                <pre>{formatToolResult(message.content)}</pre>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Assistant Message
    if (message.role === 'assistant') {
        const contentClass = `message-content ${isTypingOnly ? 'typing-only' : ''}`;
        // Direct render — matches develop/main. ReactMarkdown re-renders on
        // every content growth (driven by messages_update from the server),
        // at the browser's native paint cadence. TypingEllipsis is the dot
        // during streaming; server's `message.streaming` drives it.
        const streamingAriaProps = message.streaming
            ? { role: 'log' as const, 'aria-live': 'polite' as const, 'aria-busy': true }
            : {};
        return (
            <div className="message-bubble assistant-bubble">
                <div className="message-wrapper">
                    <div className={contentClass} {...streamingAriaProps}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                        {message.streaming && <TypingEllipsis />}
                    </div>
                    {!message.streaming && message.content && (
                        <div className="message-actions">
                            <CopyButton text={message.content} />
                            {onRegenerate && (
                                <button
                                    type="button"
                                    className="message-action-btn"
                                    onClick={() => { void onRegenerate(); }}
                                    title="Regenerate response"
                                    aria-label="Regenerate response"
                                >
                                    <RotateCcw size={14} />
                                </button>
                            )}
                        </div>
                    )}
                    {isAdminViewer && <AssistantMetaChip message={message} />}
                </div>
            </div>
        );
    }

    // User Message
    if (message.role === 'user') {
        return (
            <div className="message-bubble user-bubble">
                <div className="message-content">
                    {message.content}
                    {message.files && message.files.length > 0 && (
                        <div className="message-files">
                            {message.files.map((file) => (
                                <a 
                                    key={file.id} 
                                    href={file.file} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="message-file-item"
                                >
                                    {file.file_type.startsWith('image/') ? (
                                        <div className="file-thumbnail">
                                            <AuthenticatedImage 
                                                src={file.thumbnail || file.file} 
                                                alt={file.original_name} 
                                            />
                                        </div>
                                    ) : (
                                        <div className="file-stub">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
                                                <polyline points="13 2 13 9 20 9" />
                                            </svg>
                                            <span className="file-name">{file.original_name}</span>
                                        </div>
                                    )}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return null;
}


function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <button
            type="button"
            className="copy-btn"
            onClick={handleCopy}
            aria-label={copied ? 'Copied' : 'Copy message'}
            title="Copy"
        >
            {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
    );
}

function TypingEllipsis() {
    return (
        <span className="typing-ellipsis" aria-label="Typing">
            <span className="dot" />
            <span className="dot" />
            <span className="dot" />
        </span>
    );
}

// Helper: Format tool result JSON
function formatToolResult(content: string): string {
    try {
        const parsed = JSON.parse(content);
        return JSON.stringify(parsed, null, 2);
    } catch {
        return content;
    }
}

/** Make tool names human-readable: read_document → Reading document */
function formatToolName(name: string): string {
    const map: Record<string, string> = {
        read_document: 'Reading document',
        read_image: 'Analyzing image',
        read_document_image: 'Analyzing document image',
        lookup_terminology: 'Looking up terminology',
        lookup_semantic_terminology: 'Searching terminology',
        lookup_stage: 'Looking up stage',
        list_phases: 'Listing phases',
        list_stages: 'Listing stages',
        list_backgrounds: 'Listing backgrounds',
        set_project_stage: 'Setting project stage',
        update_project_notes: 'Updating project notes',
    };
    return map[name] || name.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
}

/** Parse tool result JSON to extract file name and markdown content */
function parseToolResult(content: string): { fileName?: string; content?: string } {
    try {
        const parsed = JSON.parse(content);
        if (parsed.file_name && parsed.content) {
            return { fileName: parsed.file_name, content: parsed.content };
        }
        if (parsed.tool && parsed.content) {
            return { fileName: parsed.file_name, content: parsed.content };
        }
    } catch {
        // Not JSON
    }
    return {};
}

/**
 * Memoization contract: non-streaming bubbles must NOT re-render when a
 * sibling bubble receives a token. We compare the shallow props that
 * actually drive the bubble's JSX — skip reference equality on
 * `tool_calls` / `files` arrays (which are rebuilt by reducer updates)
 * by checking length + first/last id.
 */
function areMessageBubblePropsEqual(
    prev: MessageBubbleProps,
    next: MessageBubbleProps,
): boolean {
    // Callback-presence check FIRST — parents flip `onRegenerate` from
    // undefined → fn (or vice versa) when "last completed assistant"
    // moves. Missing this comparison keeps a stale bubble rendered
    // without its Regenerate pill (the bug report users saw after a
    // regenerate turn landed).
    if (Boolean(prev.onRegenerate) !== Boolean(next.onRegenerate)) return false;

    const a = prev.message;
    const b = next.message;
    if (a === b) return true;
    if (a.id !== b.id) return false;
    if (a.content !== b.content) return false;
    if (a.streaming !== b.streaming) return false;
    if (a.status !== b.status) return false;
    if ((a.tool_calls?.length ?? 0) !== (b.tool_calls?.length ?? 0)) return false;
    if ((a.files?.length ?? 0) !== (b.files?.length ?? 0)) return false;
    // Usage is set once on message_complete; compare via total_tokens.
    if ((a.usage?.total_tokens ?? null) !== (b.usage?.total_tokens ?? null)) return false;
    return true;
}

const MessageBubble = memo(MessageBubbleImpl, areMessageBubblePropsEqual);

export default MessageBubble;
